'use client';

import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import HeroPrimary from '@src/components/Shared/HeroPrimary';
import Container from '@src/components/Shared/Container';
import InlineSVG from '@src/components/Shared/InlineSVG';
import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import IntegrationsCard from '@src/components/Shared/IntegrationsCard';
import SectionHeader from '@src/components/Shared/SectionHeader';
import RenderButtons from '@src/components/Shared/RenderButtons';
import NextImage from '@src/components/Shared/NextImage';

import IntegrationCardBoard from '@src/components/Sections/IntegrationCardBoard';
import CallToAction from '@src/components/Sections/CallToAction';

import usePreviewContent from '@src/hooks/usePreviewContent';

import { IntegrationsPageClientPropTypes } from './IntegrationsPage.types';
import constructURL from '@src/utils/constructURL';

const IntegrationsPageClient: React.FC<IntegrationsPageClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(
    constructURL({ languageCode: page?.languageCode?.toLowerCase(), slug: `/products/integrations`, baseURL: '' }),
  );

  setContentType('site-page');

  return (
    <>
      <HeroPrimary
        id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-[#111111]'
        attr='hero-component'
        className='bg-background-primary! mb-[0rem]! flex flex-col items-center justify-between overflow-visible! py-[3rem] md:flex-row md:px-[4rem]! md:pt-[9.3rem] md:pb-[6.3rem] lg:gap-[2rem] lg:pt-[11.2rem] lg:pb-[8.2rem] 2xl:h-[75rem]! 2xl:py-[9.6rem]'
        pretitle={page?.hero?.items?.[0]?.preTitle}
        preTitleClassName='self-center md:self-start left-[-11rem] md:left-[0]'
        title={page?.hero?.items?.[0]?.title}
        bodyCopy={page?.hero?.items?.[0]?.bodyCopy}
        image={page?.hero?.items?.[0]?.mediaPrimary}
        titleClassName='text-[4rem] lg:text-[6rem] 2xl:tracking-[-0.2432rem] leading-[4.7rem] lg:leading-[6.9rem] md:max-w-[36.6rem] md:max-w-3xl lg:max-w-[50rem] 2xl:max-w-full 2xl:tracking-[-0.152rem]'
        imgClassName='overflow-hidden absolute! sm:relative! sm:overflow-visible! flex items-end md:items-center justify-end xl:[&>img]:max-h-[60rem] top-[0] h-full md:w-[50%] opacity-[0.2] sm:opacity-[1] [&>img]:w-auto! [&>img]:max-w-full!'
        bodyCopyClassName='[&>p]:text-[1.6rem] md:[&>p]:text-[1.8rem] lg:[&>p]:text-[2rem] 2xl:[&>p]:text-[2rem] [&>p]:leading-[150%] md:max-w-[34.2rem] md:max-w-[43.7rem] lg:max-w-[51.7rem] 2xl:max-w-[59rem] relative md:top-[0] mt-[1.6rem]'
        childrenClassName='xsm:max-w-full'
      />

      <Container
        id={page?.noAdditionalCose?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-primary'
        className='flex items-center gap-[2rem] py-[2rem]'
      >
        <InlineSVG
          className='min-w-[5rem]'
          src={page?.noAdditionalCose?.items?.[0]?.mediaPrimary?.url}
          renderAt={['desktop', 'tablet', 'mobile']}
        />
        <div>
          <h2 className='text-body-copy--light mb-[1.5rem] text-[2.4rem] leading-[1.2] font-medium text-pretty'>
            {page?.noAdditionalCose?.items?.[0]?.title}
          </h2>
          <RenderBodyCopy
            bodyCopy={page?.noAdditionalCose?.items?.[0]?.bodyCopy?.json?.content}
            className='text-body-copy--light font-normal text-wrap!'
            style={{ textShadow: 'rgb(143, 66, 0, 0.2) 1px 0px 0px, rgb(143, 66, 0, 0.2) 0.540302px 0.841471px 0px' }}
          />
        </div>
      </Container>

      <Container id={page?.featuredIntegrations?.items?.[0]?.scrollTo?.slice(1)} className='my-[5rem]'>
        <h2 className='mb-[2.4rem] text-[2.8rem] leading-[3.2rem] font-medium 2xl:mb-[4rem]'>
          {page?.featuredIntegrations?.items?.[0]?.title}
        </h2>
        <div className='mt-[2rem] grid grid-cols-1 gap-[3rem] md:grid-cols-2 2xl:grid-cols-3 md:[&>div:first-child]:col-span-2 2xl:[&>div:first-child]:col-span-2 2xl:[&>div:first-child]:row-span-2'>
          {page?.featuredIntegrations?.items?.[0]?.topicCollection?.items?.map((card) => (
            <IntegrationsCard
              key={card?.sys?.id}
              title={card?.title}
              preTitle={card?.preTitle}
              subTitle={card?.subTitle}
              logo={card?.mediaPrimary}
              description={card?.bodyCopy?.json?.content}
              showDescription
              buttons={card?.buttonCollection?.items}
            />
          ))}
        </div>
      </Container>

      <Container
        id={page?.wantToIntegrate?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-background-tertiary'
        className='3xl:py-[10rem] flex flex-col items-center py-[8rem] lg:flex-row lg:gap-[2.4rem]'
      >
        <SectionHeader
          className='justify-center md:mb-[3.4rem] lg:w-[50%] lg:pb-[unset] 2xl:w-[55%]'
          pretitle={page?.wantToIntegrate?.items?.[0]?.preTitle}
          title={page?.wantToIntegrate?.items?.[0]?.title}
          titleClassName='text-[2.8rem] lg:text-[4.8rem] mb-[2.4rem] leading-[3.2rem] lg:leading-[5rem] font-light'
          align='left'
        >
          <>
            <RenderBodyCopy bodyCopy={page?.wantToIntegrate?.items?.[0]?.bodyCopy?.json?.content} />
            <RenderButtons items={page?.wantToIntegrate?.items?.[0]?.buttonCollection?.items} />
          </>
        </SectionHeader>

        <ul className='mt-[2.4rem] grid w-full grid-cols-1 gap-[2.4rem] md:mt-[unset] lg:w-[50%] 2xl:flex 2xl:w-[45%]'>
          {page?.wantToIntegrate?.items?.[0]?.extraAssetsCollection?.items?.map((card) => {
            return (
              <li key={`${card?.sys?.id}-integrations`} className='w-full'>
                <NextImage
                  className='h-[25rem] w-full overflow-hidden rounded-[4.5rem] bg-[rgba(0,0,0,0.2)] 2xl:block 2xl:h-[unset] xl:[&>img]:mt-[-20rem] 2xl:[&>img]:mt-[unset]'
                  src={card?.mediaPrimary?.url}
                  alt={card?.mediaPrimary?.description}
                  aspectRatio={card?.mediaPrimary?.width / card?.mediaPrimary?.height}
                  width={card?.mediaPrimary?.width}
                  objectFit='cover'
                >
                  <div className='text-body-copy--light 3xl:p-[4rem] p-[3rem]'>
                    <p className='3xl:text-[3.2rem] mb-[2rem] text-[2.4rem] font-medium'>{card?.title}</p>
                    <RenderBodyCopy className='text-[1.6rem] font-light' bodyCopy={card?.bodyCopy?.json?.content} />
                    <RenderButtons
                      className='3xl:bottom-[4rem] 3xl:right-[4rem] absolute right-[3rem] bottom-[3rem] items-end!'
                      btnClassName='text-body-copy--light!'
                      svgClassName='text-body-copy--light!'
                      items={card?.buttonCollection?.items}
                    />
                  </div>
                </NextImage>
              </li>
            );
          })}
        </ul>
      </Container>

      <IntegrationCardBoard data={page?.integrationsCard?.items?.[0]} globalTextBlock={data?.globalTextBlock} />

      <CallToAction
        className='max-w-[59.8rem]'
        id={page?.callToAction?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.callToAction?.items?.[0]?.title}
        bodyCopy={page?.callToAction?.items?.[0]?.bodyCopy?.json?.content}
        buttons={page?.callToAction?.items?.[0]?.buttonCollection?.items}
      />
    </>
  );
};

export default IntegrationsPageClient;
